/**
 * Parses a comma-separated string into an array of trimmed, non-empty tags
 */
export const parseTagsFromString = (value: string): string[] =>
  value
    ? value
        .split(",")
        .map(tag => tag.trim())
        .filter(tag => tag !== "")
    : [];

export const getEvaluateTagId = (tag: string): string | null => {
  const match = tag.match(/^evaluate-(\d+)$/);
  return match ? match[1] : null;
};

export const getWorkflowTagId = (tag: string): string | null => {
  const match = tag.match(/^workflow-(\d+)$/);
  return match ? match[1] : null;
};

/** Parses composite table tags `smart-table:{tableId}:{sheetId}` and `table:{tableId}:{sheetId}` */
export const parseSmartTableTag = (tag: string): { tableId: string; sheetId: string } | null => {
  const match = tag.match(/^(?:smart-table|table):([0-9a-f-]{36}):([0-9a-f-]{36})$/);
  return match ? { tableId: match[1], sheetId: match[2] } : null;
};

export const isLinkTag = (tag: string): boolean =>
  getEvaluateTagId(tag) !== null ||
  getWorkflowTagId(tag) !== null ||
  parseSmartTableTag(tag) !== null;

export const sortTagsWithLinksFirst = (tags: string[]): string[] => {
  const linkTags: string[] = [];
  const regularTags: string[] = [];

  tags.forEach(tag => {
    if (isLinkTag(tag)) {
      linkTags.push(tag);
    } else {
      regularTags.push(tag);
    }
  });

  return [...linkTags, ...regularTags];
};

export interface TagProcessingResult {
  tag: string;
  isLink: boolean;
  displayText: string;
  linkPath: string | null;
}

const getDisplayText = (
  evaluateId: string | null,
  workflowId: string | null,
  smartTable: { tableId: string; sheetId: string } | null,
  tag: string,
): string => {
  if (evaluateId !== null) {
    return "Open eval";
  }
  if (workflowId !== null) {
    return "Open workflow";
  }
  if (smartTable !== null) {
    return "Open table";
  }
  return tag;
};

export interface EvaluateTagMetadata {
  is_blueprint: boolean;
  parent_report_id: number | null;
  workspace_id: number;
}

export const processTag = (
  tag: string,
  workspaceId: string | undefined,
  evaluateMetadataMap?: Record<string, EvaluateTagMetadata>,
): TagProcessingResult => {
  const evaluateId = getEvaluateTagId(tag);
  const workflowId = getWorkflowTagId(tag);
  const smartTable = parseSmartTableTag(tag);
  const isLink = evaluateId !== null || workflowId !== null || smartTable !== null;

  // Get display text
  const displayText = getDisplayText(evaluateId, workflowId, smartTable, tag);

  // Get link path
  let linkPath: string | null = null;
  if (workspaceId) {
    if (evaluateId) {
      const metadata = evaluateMetadataMap?.[evaluateId];
      if (metadata) {
        if (metadata.is_blueprint) {
          linkPath = `/workspace/${workspaceId}/evaluate/blueprints/${evaluateId}`;
        } else if (metadata.parent_report_id) {
          linkPath = `/workspace/${workspaceId}/evaluate/blueprints/${metadata.parent_report_id}/reports/${evaluateId}`;
        } else {
          linkPath = `/workspace/${workspaceId}/evaluate/blueprints/${evaluateId}`;
        }
      } else {
        linkPath = `/workspace/${workspaceId}/evaluate/blueprints/${evaluateId}`;
      }
    } else if (workflowId) {
      linkPath = `/workspace/${workspaceId}/workflow/${workflowId}`;
    } else if (smartTable) {
      linkPath = `/workspace/${workspaceId}/smart-tables/${smartTable.tableId}?sheet=${smartTable.sheetId}`;
    }
  }

  return {
    tag,
    isLink,
    displayText,
    linkPath,
  };
};
